import { aiScore } from './prepackCardModel'
import { isPrepackSurfaceLive } from './prepackSurfaceLive'

const WHITESPACE_PATTERN = /\s+/
const MATCHUP_SEPARATOR_PATTERN = /\s+(?:vs\.?|v\.?|@|at|-)\s+/i

const KNOWN_ACRONYM_LEAGUES = new Set(['NFL', 'NBA', 'MLB', 'NHL', 'WNBA', 'CFL', 'MLS', 'UFC', 'NCAAF', 'NCAAB', 'EPL'])

export function humanizeLeague(league) {
  if (!league || league === '—') return null
  const trimmed = String(league).trim()
  if (!trimmed) return null
  const upper = trimmed.toUpperCase()
  if (KNOWN_ACRONYM_LEAGUES.has(upper) || trimmed.length <= 4) return upper
  return trimmed
    .replaceAll('_', ' ')
    .replaceAll('-', ' ')
    .split(WHITESPACE_PATTERN)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ')
}

export function humanizeSport(sport) {
  if (!sport || sport === '—') return null
  const trimmed = String(sport).trim()
  if (!trimmed) return null
  const upper = trimmed.toUpperCase()
  if (upper === 'MMA' || upper === 'UFC') return upper
  return trimmed
    .replaceAll('_', ' ')
    .replaceAll('-', ' ')
    .split(WHITESPACE_PATTERN)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ')
}

export function eventGroup(event) {
  const league = humanizeLeague(event?.league)
  if (league) return league
  const sport = humanizeSport(event?.sport)
  if (sport) return sport
  return 'Other'
}

// Identity of the game a leg belongs to. Two games can share a label (a
// series, or the same matchup on another date), so the provider event id is
// the key and the label is only what the operator reads.
const eventKey = (leg) => String(leg?.event_id ?? leg?.eventId ?? leg?.event_label ?? '')

function packEventLegs(pack) {
  const legs = (pack.legs || []).filter((leg) => Boolean(leg?.event_label))
  if (legs.length > 0) return legs
  if (pack.event_label && pack.event_label !== '—') {
    return [{ event_id: pack.event_id, event_label: pack.event_label, league: pack.league, sport: pack.sport, event_start_time: pack.event_start_time }]
  }
  return []
}

/** Keys of every event a pack has a leg in. */
export function packEventKeys(pack) {
  return new Set(packEventLegs(pack).map(eventKey))
}

/**
 * One option per event across the given packs: `value` is the event key,
 * `eventId` the provider event id when the key is one (null when only the
 * label names the game), `count` the packs with a leg in it, earliest
 * kickoff first.
 */
export function eventOptions(packs = []) {
  const events = new Map()
  for (const pack of packs || []) {
    const seenInPack = new Set()
    for (const leg of packEventLegs(pack)) {
      const key = eventKey(leg)
      let entry = events.get(key)
      if (!entry) {
        const eventId = leg?.event_id ?? leg?.eventId
        entry = { value: key, eventId: eventId == null || eventId === '' ? null : String(eventId), label: leg.event_label, league: null, sport: null, startTime: null, count: 0, _teamNames: new Set() }
        events.set(key, entry)
      }
      entry.league ||= leg.league || pack.league || null
      entry.sport ||= leg.sport || pack.sport || null
      const incomingTime = leg.event_start_time || pack.event_start_time
      if (incomingTime) {
        const current = Date.parse(entry.startTime)
        const incoming = Date.parse(incomingTime)
        if (!entry.startTime || (Number.isFinite(incoming) && (!Number.isFinite(current) || incoming < current))) entry.startTime = incomingTime
      }
      for (const name of [leg.participant, leg.selection_name, leg.team, ...leg.event_label.split(MATCHUP_SEPARATOR_PATTERN)]) {
        if (name && String(name).trim()) entry._teamNames.add(String(name).trim())
      }
      if (!seenInPack.has(key)) {
        seenInPack.add(key)
        entry.count += 1
      }
    }
  }

  const list = [...events.values()].map(({ _teamNames, ...rest }) => ({ ...rest, teamNames: [..._teamNames] }))
  return list.sort((a, b) => {
    const timeA = Date.parse(a.startTime)
    const timeB = Date.parse(b.startTime)
    const hasA = Number.isFinite(timeA)
    const hasB = Number.isFinite(timeB)
    if (hasA && hasB && timeA !== timeB) return timeA - timeB
    if (hasA !== hasB) return hasA ? -1 : 1
    return a.label.localeCompare(b.label)
  })
}
export function groupValues(pack, dimension) {
  if (!dimension) return []
  const field = dimension === 'event' ? 'event_label' : dimension
  const values = (pack.legs || []).map((leg) => leg[field]).filter(Boolean)
  if (!values.length && pack[dimension] && pack[dimension] !== '—') values.push(pack[dimension])
  return [...new Set(values.map((value) => dimension === 'league' ? value.toUpperCase() : dimension === 'sport' ? value.charAt(0).toUpperCase() + value.slice(1) : value))]
}
/**
 * The page an embed names for a Surfaces filter choice, as the widget sends
 * it (`sport`, `league`, `event_id`), or null when there is none: a game by
 * its provider id (a game known only by its label has no page), a league
 * with its sport when its packs' legs agree on one (an embed names a league
 * page with both, and that is its own lineup), a sport alone. `value` is the
 * filter's (groupValues for a sport or league, the event key for a game).
 */
export function filterPageOf(dimension, value, packs = [], events = []) {
  if (!dimension || !value) return null
  if (dimension === 'event') {
    const eventId = events.find((event) => event.value === value)?.eventId
    return eventId ? { event_id: eventId } : null
  }
  if (dimension === 'league') {
    const sports = new Set((packs || []).flatMap((pack) => (pack.legs || [])
      .filter((leg) => String(leg.league || '').toUpperCase() === value)
      .map((leg) => String(leg.sport || '').trim().toLowerCase())
      .filter(Boolean)))
    return sports.size === 1 ? { sport: [...sports][0], league: value } : { league: value }
  }
  return { sport: value.toLowerCase() }
}

export function rankSurface(packs, surface) {
  const locks = surface.layout_config?.locked_positions || {}
  const mode = surface.layout_config?.ranking_mode || 'ai'
  const sorted = [...packs].sort((a, b) => (mode === 'recency' ? 0 : (aiScore(b) ?? -1) - (aiScore(a) ?? -1)) || Date.parse(b.created_at || 0) - Date.parse(a.created_at || 0) || String(a.id).localeCompare(String(b.id)))
  const result = Array(packs.length)
  const unlocked = []
  for (const pack of sorted) {
    const index = Number(locks[pack.id]) - 1
    if (Number.isInteger(index) && index >= 0 && index < result.length && !result[index]) result[index] = pack
    else unlocked.push(pack)
  }
  let cursor = 0
  for (let index = 0; index < result.length; index += 1) if (!result[index]) result[index] = unlocked[cursor++]
  return result
}

/**
 * The surfaces a pack is on, as the remove dialog lists them (#999): the
 * surface the operator is on first, then the rest in sidebar order, then
 * slugs the organization has no surface for any more (deleted in Design),
 * which are still assignments and so still removable. The current surface is
 * listed even when a stale pack lacks it: Remove started there.
 */
export function packSurfaceOptions(pack, surfaces = [], currentSlug = null) {
  const assigned = new Set([...(currentSlug ? [currentSlug] : []), ...(pack?.surfaces || [])])
  const known = new Set(surfaces.map((surface) => surface.slug))
  const options = [
    ...surfaces.filter((surface) => assigned.has(surface.slug)).map((surface) => ({ slug: surface.slug, name: surface.name || surface.slug, live: isPrepackSurfaceLive(surface), known: true })),
    ...[...assigned].filter((slug) => !known.has(slug)).map((slug) => ({ slug, name: slug, live: false, known: false })),
  ].map((option) => ({ ...option, current: option.slug === currentSlug }))
  return [...options.filter((option) => option.current), ...options.filter((option) => !option.current)]
}

/** The toast after a removal: how many surfaces the pack left, and whether it went back to Draft. */
export function removalMessage(saved, count) {
  if (saved?.status === 'draft') return count > 1 ? 'Prepack removed from its last surfaces and moved to Draft' : 'Prepack removed from its last surface and moved to Draft'
  return count > 1 ? `Prepack removed from ${count} surfaces` : 'Prepack removed from surface'
}
